import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * AutoCarousel - Auto-sliding carousel with 4 columns
 * @param {Object} props
 * @param {React.ReactNode} props.children - Carousel items
 * @param {number} props.autoPlayInterval - Auto-play interval in ms (default: 4000)
 * @param {boolean} props.pauseOnHover - Pause on hover (default: true)
 */
export const AutoCarousel = ({ children, autoPlayInterval = 4000, pauseOnHover = true }) => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const itemsArray = React.Children.toArray(children);
  
  const checkScrollPosition = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);

  const scrollToNext = useCallback(() => {
    if (scrollContainerRef.current) {
      // Batch DOM reads before writes to prevent forced reflow
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      const cardWidth = scrollContainerRef.current.firstChild?.firstChild?.offsetWidth || 300;
      
      // Use requestAnimationFrame to batch writes after reads
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          if (scrollLeft >= scrollWidth - clientWidth - 10) {
            // Reset to start
            scrollContainerRef.current.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            scrollContainerRef.current.scrollBy({ left: cardWidth + 24, behavior: 'smooth' });
          }
          setTimeout(checkScrollPosition, 400);
        }
      });
    }
  }, [checkScrollPosition]);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      // Batch DOM reads before writes to prevent forced reflow
      const cardWidth = scrollContainerRef.current.firstChild?.firstChild?.offsetWidth || 300;
      
      // Use requestAnimationFrame to batch writes after reads
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          if (direction === 'left') {
            scrollContainerRef.current.scrollBy({ left: -(cardWidth + 24), behavior: 'smooth' });
          } else {
            scrollToNext();
          }
          setTimeout(checkScrollPosition, 400);
        }
      });
    }
  };

  // Auto-play effect
  useEffect(() => {
    if (isPaused || itemsArray.length <= 4) return;
    
    const interval = setInterval(() => {
      scrollToNext();
    }, autoPlayInterval);
    
    return () => clearInterval(interval);
  }, [isPaused, autoPlayInterval, scrollToNext, itemsArray.length]);

  useEffect(() => {
    checkScrollPosition();
    window.addEventListener('resize', checkScrollPosition);
    return () => window.removeEventListener('resize', checkScrollPosition);
  }, [children, checkScrollPosition]);

  return (
    <div 
      className="relative" 
      data-testid="auto-carousel"
      onMouseEnter={() => pauseOnHover && setIsPaused(true)}
      onMouseLeave={() => pauseOnHover && setIsPaused(false)}
    >
      {showLeftArrow && (
        <button 
          onClick={() => scroll('left')} 
          className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white shadow-lg border border-[#E5E7EB] hover:shadow-xl hover:border-[#F97316] transition-all" 
          aria-label="Previous" 
          data-testid="carousel-prev"
        >
          <svg className="w-5 h-5 text-[#111827]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      {showRightArrow && (
        <button 
          onClick={() => scroll('right')} 
          className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white shadow-lg border border-[#E5E7EB] hover:shadow-xl hover:border-[#F97316] transition-all" 
          aria-label="Next" 
          data-testid="carousel-next"
        >
          <svg className="w-5 h-5 text-[#111827]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
      <div 
        ref={scrollContainerRef} 
        onScroll={checkScrollPosition} 
        className="overflow-x-auto scrollbar-hide scroll-smooth"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className="flex gap-6 py-2 px-1">
          {itemsArray.map((child, index) => (
            <div key={index} className="flex-shrink-0 w-[calc(25%-18px)] min-w-[280px]">
              {child}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
